

function pemToArrayBuffer(pem) {
  // Remove header/footer + whitespace
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s/g, "");

  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i+=1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

async function importPublicKey() {
  const publicKeyPem = process.env.REACT_APP_PUBLIC_KEY;
  if (!publicKeyPem) {
    throw new Error("Missing PUBLIC_KEY in frontend .env");
  }

  // Convert \n to actual newlines (if stored escaped in .env)
  const normalizedPem = publicKeyPem.replace(/\\n/g, "\n");

  const keyBuffer = pemToArrayBuffer(normalizedPem);

  const importedKey = await window.crypto.subtle.importKey(
    "spki",
    keyBuffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"]
  );
  return importedKey;
}

async function generateAesKey() {
    const generatedKey = await crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
    return generatedKey;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach(b => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

async function exportAesKeyRaw(key) {
  const raw = await crypto.subtle.exportKey("raw", key);
  return new Uint8Array(raw);
}

async function encryptWithAes(key, data) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(data);

  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded
  );

  const encryptedBytes = new Uint8Array(encryptedBuffer);

  // Last 16 bytes = auth tag
  const tag = encryptedBytes.slice(encryptedBytes.length - 16);
  const ciphertext = encryptedBytes.slice(0, encryptedBytes.length - 16);

  return { iv, ciphertext, tag };
}

async function encryptAesKeyWithRsa(publicKey, aesKeyRaw) {
    const encryptedKey = await crypto.subtle.encrypt(
        { name: "RSA-OAEP" },
        publicKey,
        aesKeyRaw
    );
    return encryptedKey; 
}

export async function encryptDataV1(plainText) {
  const publicKey = await importPublicKey();

  // 1️⃣ Generate AES key
  const aesKey = await generateAesKey();

  // 2️⃣ Encrypt message using AES
  const { iv, ciphertext, tag } = await encryptWithAes(aesKey, plainText);

  // 3️⃣ Export AES key raw
  const aesKeyRaw = await exportAesKeyRaw(aesKey);

  // 4️⃣ Encrypt AES key using RSA
  const encryptedKey = await encryptAesKeyWithRsa(publicKey, aesKeyRaw);

  return {
    encryptedKey: arrayBufferToBase64(encryptedKey),
    iv: arrayBufferToBase64(iv),
    ciphertext: arrayBufferToBase64(ciphertext),
    tag: arrayBufferToBase64(tag),
  };
}
